// src/dominio/coloresProducto.js
// Muestras de color de la tienda: el admin escribe el color como texto
// («Negro», «Azul marino», «Negro/Blanco») y puede elegir su tono exacto con la
// paleta (producto.tonosColor). Sin tono elegido, aquí se deduce del nombre; un
// nombre que no se reconoce no inventa un color: la tienda lo muestra como
// texto. Los tonos son datos del producto, no del tema.

const TONOS = {
  negro: '#1c1c1c', black: '#1c1c1c', blanco: '#ffffff', white: '#ffffff',
  hueso: '#f2ebdd', marfil: '#f4eedd', crema: '#f3e5c7', perla: '#ece8e1',
  beige: '#d9c3a0', arena: '#cdb78f', nude: '#e3bc9a', camel: '#b98a52',
  kaki: '#b5a27a', caqui: '#b5a27a', khaki: '#b5a27a', topo: '#8b7d6b', taupe: '#8b7d6b',
  marron: '#6b4226', cafe: '#6f4a2f', chocolate: '#4e2e1e', terracota: '#b5583a', cobre: '#b87333',
  gris: '#8c8c8c', 'gris claro': '#c8c8c8', 'gris oscuro': '#4a4a4a', plomo: '#7a7d80',
  melange: '#a9a9a9', jaspeado: '#a9a9a9', 'gris melange': '#a9a9a9', 'gris jaspeado': '#a9a9a9',
  grafito: '#3a3b3d', carbon: '#2e2e30', acero: '#71797e', plata: '#c0c0c0', plateado: '#c0c0c0',
  dorado: '#c9a23f', oro: '#c9a23f',
  rojo: '#d0202a', 'rojo oscuro': '#8e1b22', vino: '#6d1a2b', guinda: '#5e1224', granate: '#7b1e2b',
  burdeo: '#6a1b2a', burdeos: '#6a1b2a', borgona: '#6a1b2a', cereza: '#a4133c',
  coral: '#f2725e', salmon: '#f4987a', durazno: '#f6b58e', melon: '#f6a26b',
  naranja: '#f07a1a', anaranjado: '#f07a1a', mostaza: '#d4a017', amarillo: '#f4d03f', limon: '#e3e85a',
  verde: '#2e8b3e', 'verde oscuro': '#1e5631', 'verde claro': '#8fd18f', 'verde militar': '#4b5320',
  militar: '#4b5320', olivo: '#6b7a3a', oliva: '#6b7a3a', 'verde oliva': '#6b7a3a', 'verde botella': '#0f4d2e',
  menta: '#a8e6cf', 'verde agua': '#7fd8be', 'verde menta': '#a8e6cf', esmeralda: '#0f8a5f', 'verde neon': '#39ff14',
  'verde limon': '#9acd32', turquesa: '#1fb5ad', aqua: '#36c9c6', jade: '#00a86b',
  celeste: '#7ec8e3', 'azul claro': '#7ec8e3', azul: '#1f4fb0', 'azul oscuro': '#1b2a4a', 'azul marino': '#1b2a4a',
  marino: '#1b2a4a', navy: '#1b2a4a', 'azul electrico': '#2d5bff', azulino: '#3a6fd8', 'azul rey': '#2a4fc2',
  petroleo: '#1e4d5c', 'azul petroleo': '#1e4d5c', jean: '#3b5b85', denim: '#3b5b85', indigo: '#3f3d8f',
  morado: '#6a2c91', lila: '#b79cd9', lavanda: '#c7b8e6', violeta: '#7f4fc9', purpura: '#6a1b9a', uva: '#5b2a6e',
  fucsia: '#d6247a', magenta: '#c2185b', rosa: '#f2a7c0', rosado: '#f2a7c0', 'rosa palo': '#d8a7a0',
  'palo rosa': '#d8a7a0', 'rosa claro': '#f7c6d6', 'rosa neon': '#ff4fa3', frambuesa: '#c72c5a',
};

// Estampados conocidos: varias franjas en un mismo círculo.
const ESTAMPADOS = {
  camuflado: ['#4b5320', '#b5a27a', '#6b4226'], camo: ['#4b5320', '#b5a27a', '#6b4226'],
  multicolor: ['#d0202a', '#f4d03f', '#2e8b3e', '#1f4fb0'], arcoiris: ['#d0202a', '#f4d03f', '#2e8b3e', '#1f4fb0'],
  cebra: ['#1c1c1c', '#ffffff'], leopardo: ['#c9a23f', '#4e2e1e'], animal: ['#c9a23f', '#4e2e1e'],
};

const normalizar = (texto) => String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9/ -]+/g, ' ').replace(/\s+/g, ' ').trim();

// Solo entradas propias del diccionario: «constructor» o «toString» no son colores.
const propio = (diccionario, clave) => (Object.prototype.hasOwnProperty.call(diccionario, clave) ? diccionario[clave] : undefined);

const tonoDe = (parte) => {
  const nombre = normalizar(parte);
  if (propio(TONOS, nombre)) return [propio(TONOS, nombre)];
  if (propio(ESTAMPADOS, nombre)) return propio(ESTAMPADOS, nombre);
  // «Negro mate», «Azul jaspeado»: se reconoce por las primeras palabras.
  const palabras = nombre.split(' ');
  for (let n = palabras.length - 1; n > 0; n -= 1) {
    const base = propio(TONOS, palabras.slice(0, n).join(' '));
    if (base) return [base];
  }
  return null;
};

/**
 * Tonos de un color escrito por el admin, o null si no se reconoce.
 * «Negro/Blanco», «Negro y rojo» o «Negro con blanco» dan un círculo partido
 * (hasta 3 tonos); basta con que una parte no se reconozca para mostrar texto.
 */
export const tonosColor = (nombre) => {
  const partes = normalizar(nombre).split(/\s*(?:\/|\by\b|\bcon\b|-)\s*/).filter(Boolean);
  if (!partes.length || partes.length > 3) return null;
  const tonos = [];
  for (const parte of partes) {
    const tono = tonoDe(parte);
    if (!tono) return null;
    tonos.push(...tono);
  }
  return tonos.slice(0, 4);
};

// Tono elegido por el admin con la paleta: manda sobre el que se deduce del nombre.
export const TONO_HEX = /^#[0-9a-f]{6}$/i;
/** Valor inicial de la paleta para un nombre sin tono conocido (gris medio). */
export const TONO_NEUTRO = '#9ca3af';
const tonoValido = (valor) => typeof valor === 'string' && TONO_HEX.test(valor);

/** Tono que se deduce del nombre (para sugerirlo en la paleta), o null. */
export const tonoSugerido = (nombre) => {
  const tonos = tonosColor(nombre);
  return tonos?.length === 1 ? tonos[0] : null;
};

/** Tonos de la muestra: el elegido por el admin o, sin él, los del nombre. */
export const tonosMuestra = (color, tonosElegidos) => {
  const elegido = tonosElegidos && Object.prototype.hasOwnProperty.call(tonosElegidos, color) ? tonosElegidos[color] : null;
  return tonoValido(elegido) ? [elegido.toLowerCase()] : tonosColor(color);
};

/** Solo los tonos de colores que existen, en hex #rrggbb (lo que se guarda en el producto). */
export const normalizarTonosColor = (tonos, colores) => {
  if (!tonos || typeof tonos !== 'object' || Array.isArray(tonos)) return {};
  return Object.fromEntries((colores || [])
    .filter((color) => Object.prototype.hasOwnProperty.call(tonos, color) && tonoValido(tonos[color]))
    .map((color) => [color, tonos[color].toLowerCase()]));
};

/** Nombre para mostrar («azul» → «Azul»); el valor guardado no cambia. */
export const nombreColor = (nombre) => {
  const texto = String(nombre || '').trim();
  return texto.charAt(0).toLocaleUpperCase('es-PE') + texto.slice(1);
};

/** Fondo CSS del círculo: un tono liso, mitades en diagonal o sectores. */
export const fondoMuestra = (tonos) => {
  if (!tonos?.length) return '';
  if (tonos.length === 1) return tonos[0];
  if (tonos.length === 2) return `linear-gradient(135deg, ${tonos[0]} 50%, ${tonos[1]} 50%)`;
  const paso = 100 / tonos.length;
  return `conic-gradient(${tonos.map((t, i) => `${t} ${i * paso}% ${(i + 1) * paso}%`).join(', ')})`;
};
